import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { RevealLine } from "@/components/RevealLine";

const CHAPTERS = [
  {
    title: "Built On The Tools",
    body: "Offcut started on site, not in a boardroom. It was built by tradies who got sick of digging through folders for a formula, losing logbook pages, and redrawing plans on scrap timber. Every tool in the app exists because one of us needed it on a real job.",
  },
  {
    title: "What Offcut Does",
    body: "Building formulas and calculators for the maths you do every day. Logbooks and daily building logs that actually stay organised. Standards and tolerances at your fingertips with NCC bookmarks. Drafting tools to sketch a plan and save it properly. All in one app, in your pocket.",
  },
  {
    title: "Made For Crews",
    body: "Whether you're a solo chippy or running a full crew, Offcut scales with you. Share bookmarks, drafts and daily logs across the team, keep one simple bill, and roll job hours up for the boss — so everyone spends less time on paperwork and more time on the tools.",
  },
  {
    title: "Why We Keep Building",
    body: "The ultimate building app is never finished. We ship updates based on what tradies actually ask for, and we're always keen to hear what would make your day on site easier. Flick us a message any time via the Contact Us page.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: [0.76, 0, 0.24, 1] },
};

export default function About() {
  return (
    <motion.main
      data-testid="about-page"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
      className="relative z-10 mx-auto max-w-3xl px-6 pb-32 pt-32 sm:px-10"
    >
      <Link
        to="/"
        data-testid="page-logo-home-link"
        aria-label="Back to home"
        className="block w-fit rounded-lg transition-transform duration-300 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
      >
        <img
          src="/chippymate-logo.png"
          alt="Offcut — back to home"
          className="h-auto w-28"
        />
      </Link>
      <p className="mt-2 text-xs font-semibold uppercase tracking-[0.35em] text-white/50">
        Offcut - ChippyMate
      </p>
      <h1 className="font-brand mt-3 text-6xl tracking-wide text-white sm:text-7xl">
        <RevealLine>About</RevealLine>
      </h1>

      <motion.section {...fadeUp} className="mt-14" data-testid="about-chippymate">
        <h2 className="font-brand text-4xl tracking-wide text-white sm:text-5xl">
          About ChippyMate
        </h2>
        <div className="mt-6 space-y-5 text-sm leading-relaxed text-white/70 sm:text-base">
          <p>
            ChippyMate is the company behind Offcut, an app designed to make
            everyday tasks easier for tradies, handymen and DIY enthusiasts
            alike.
          </p>
          <p>
            At the heart of ChippyMate is a hands-on approach to
            problem-solving, a passion for innovation and a commitment to
            building tools that work in the real world. Offcut is our first
            step towards transforming the way projects are approached by
            simplifying everyday tasks, helping tradies stay organised, and
            bringing essential tools and resources together in one place.
          </p>
          <p>
            We're passionate about turning real-world trade needs into
            practical digital solutions. Through the ongoing development of
            Offcut, we're continually finding better ways to help you work
            smarter, tackle projects with confidence and get the job done.
          </p>
        </div>
        <p className="mt-8 text-base font-bold tracking-wide text-white sm:text-lg">
          ChippyMate develops it.{" "}
          <span className="text-[#5ee5f2]">Offcut delivers it.</span>
        </p>
      </motion.section>

      <motion.section
        {...fadeUp}
        className="mt-16 border-t border-white/10 pt-10"
        data-testid="about-offcut"
      >
        <h2 className="font-brand text-4xl tracking-wide text-white sm:text-5xl">
          Offcut
        </h2>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base">
          Everything you need to build, create, and get the job done.
        </p>
      </motion.section>

      <div className="mt-16 space-y-12">
        {CHAPTERS.map((chapter, i) => (
          <motion.section
            key={chapter.title}
            {...fadeUp}
            className="border-t border-white/10 pt-8"
          >
            <div className="flex items-baseline gap-5">
              <span className="font-brand text-2xl text-white/35">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h2 className="font-brand text-3xl tracking-wide text-white sm:text-4xl">
                {chapter.title}
              </h2>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-white/70 sm:text-base">
              {chapter.body}
            </p>
          </motion.section>
        ))}
      </div>
    </motion.main>
  );
}
